import { useId, useState, type ReactNode } from "react";
import { errorMessage } from "../../shared/api/errors.ts";
import type { Device } from "../../shared/devices/devices.ts";
import { formatDayMonth, pluralRu } from "../../shared/format.ts";
import {
  inviteLink,
  useCreateInvite,
  useGuestAction,
  useGuests,
  useInvites,
  useRevokeInvite,
  type Guest,
  type GuestAction,
  type Invite,
} from "../../shared/devices/sharing.ts";
import { useSession } from "../../shared/session/SessionProvider.tsx";
import { Avatar } from "../../shared/ui/Avatar/Avatar.tsx";
import { BottomSheet } from "../../shared/ui/BottomSheet/BottomSheet.tsx";
import { Button } from "../../shared/ui/Button/Button.tsx";
import { CloseIcon, MoreIcon } from "../../shared/ui/icons.tsx";
import { Menu } from "../../shared/ui/Menu/Menu.tsx";
import { Spinner } from "../../shared/ui/Spinner/Spinner.tsx";
import { StatusBadge } from "../../shared/ui/StatusBadge/StatusBadge.tsx";
import { InviteSheet } from "./InviteSheet.tsx";
import styles from "./Owners.module.css";

/**
 * Блок «Владельцы» (Figma 560:3036), только у владельца E-va: он сам, активные и заблокированные гости
 * и ожидающие invite. Статус `fault` и роли вида «Гость • Управление экраном» из макета в MVP нет.
 */
export function Owners({ device }: { device: Device }) {
  const titleId = useId();
  const { user } = useSession();
  const guests = useGuests(device.id);
  const invites = useInvites(device.id);
  const guestAction = useGuestAction(device.id);
  const revokeInvite = useRevokeInvite(device.id);
  const createInvite = useCreateInvite(device.id);
  const [link, setLink] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Guest | null>(null);
  // Ошибка последнего действия: следующее действие её сбрасывает.
  const [actionError, setActionError] = useState<Error | null>(null);
  // Действия с гостями и отзыв invite идут по одному: колбэки `mutate` TanStack Query вызывает только у последнего
  // вызова, и ошибка прежнего действия иначе потерялась бы.
  const guestBusy = guestAction.isPending;

  function changeGuest(guest: Guest, action: GuestAction, onSettled?: () => void) {
    setActionError(null);
    guestAction.mutate({ userId: guest.userId, action }, { onError: setActionError, onSettled });
  }

  function revoke(invite: Invite) {
    setActionError(null);
    revokeInvite.mutate(invite.id, { onError: setActionError });
  }

  function sendInvite() {
    setActionError(null);
    createInvite.mutate(undefined, {
      onSuccess: ({ token }) => setLink(inviteLink(token)),
      onError: setActionError,
    });
  }

  const loadError = guests.error ?? invites.error;
  // Доступ к E-va есть у владельца и у его гостей, включая заблокированных; ожидающий invite — ещё не доступ.
  const accessCount = guests.data ? 1 + guests.data.length : null;

  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <div className={styles.header}>
        <h2 id={titleId} className={styles.title}>
          Владельцы
        </h2>
        <div className={styles.headerActions}>
          {accessCount !== null && <span className={styles.count}>{accessLabel(accessCount)}</span>}
          <button
            type="button"
            className={styles.sendInvite}
            aria-busy={createInvite.isPending || undefined}
            disabled={createInvite.isPending}
            onClick={sendInvite}
          >
            Отправить invite
          </button>
        </div>
      </div>
      {actionError && (
        <p className={styles.error} role="alert">
          {errorMessage(actionError)}
        </p>
      )}
      <ul className={styles.list}>
        {user && (
          <PersonRow
            avatarId={user.id}
            name={user.displayName}
            role="Основной владелец"
            detail={user.email}
            badge={<StatusBadge tone="success">active</StatusBadge>}
          />
        )}
        {guests.data?.map((guest) => {
          const name = guestName(guest);
          const blocked = guest.status === "blocked";
          return (
            <PersonRow
              key={guest.id}
              avatarId={guest.userId}
              name={name}
              role="Гость"
              detail={guest.email}
              badge={blocked ? <StatusBadge tone="error">blocked</StatusBadge> : <StatusBadge tone="success">active</StatusBadge>}
            >
              <Menu
                label={`Действия с гостем ${name}`}
                trigger={<MoreIcon />}
                disabled={guestBusy}
                items={[
                  blocked
                    ? { label: "Разблокировать", onSelect: () => changeGuest(guest, "unblock") }
                    : { label: "Заблокировать", onSelect: () => changeGuest(guest, "block") },
                ]}
              />
              <IconButton label={`Удалить гостя ${name}`} disabled={guestBusy} onClick={() => setRemoving(guest)}>
                <CloseIcon />
              </IconButton>
            </PersonRow>
          );
        })}
        {invites.data?.map((invite) => (
          <PersonRow
            key={invite.id}
            avatarId={invite.id}
            name={invite.label?.trim() || "Приглашение"}
            role="Ждёт подтверждения"
            detail={inviteExpiry(invite)}
            badge={<StatusBadge tone="warning">pending</StatusBadge>}
          >
            {/* Подписи строк invite совпадают — кнопку отличает время выдачи. */}
            <IconButton
              label="Отозвать invite"
              description={`Выдан ${issuedFormat.format(new Date(invite.createdAt))}`}
              disabled={revokeInvite.isPending}
              onClick={() => revoke(invite)}
            >
              <CloseIcon />
            </IconButton>
          </PersonRow>
        ))}
      </ul>
      {loadError ? (
        <div className={styles.loadError}>
          <p className={styles.error} role="alert">
            {errorMessage(loadError)}
          </p>
          <Button
            variant="secondary"
            onClick={() => {
              if (guests.isError) void guests.refetch();
              if (invites.isError) void invites.refetch();
            }}
          >
            Повторить
          </Button>
        </div>
      ) : (
        (guests.isPending || invites.isPending) && (
          <div className={styles.loading} role="status" aria-label="Загрузка гостей">
            <Spinner />
          </div>
        )
      )}
      <InviteSheet link={link} onClose={() => setLink(null)} />
      <RemoveGuestSheet
        guest={removing}
        pending={guestBusy}
        onConfirm={(guest) => changeGuest(guest, "remove", () => setRemoving(null))}
        onClose={() => setRemoving(null)}
      />
    </section>
  );
}

type PersonRowProps = {
  /** От него зависит цвет аватара. */
  avatarId: string;
  name: string | null;
  role: string;
  /** Третья строка: email человека или срок invite; `null` — строки нет. */
  detail: string | null;
  badge: ReactNode;
  children?: ReactNode;
};

/** User / Owner Card (Figma 444:3497): аватар, имя, роль, email, статус и действия. */
function PersonRow({ avatarId, name, role, detail, badge, children }: PersonRowProps) {
  return (
    <li className={styles.row}>
      <Avatar userId={avatarId} name={name} size={42} />
      <div className={styles.text}>
        <span className={styles.name}>{name}</span>
        <span className={styles.role}>{role}</span>
        {detail && <span className={styles.detail}>{detail}</span>}
      </div>
      <div className={styles.actions}>
        {badge}
        {children}
      </div>
    </li>
  );
}

function IconButton({
  label,
  description,
  disabled,
  onClick,
  children,
}: {
  label: string;
  /** Уточнение для скринридера, когда одинаковых кнопок несколько. */
  description?: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={styles.iconButton}
      aria-label={label}
      title={description}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** Подтверждение удаления гостя: каркас 09.07 / DEVICE / Delete User (Figma 1053:27293) в шторке. */
function RemoveGuestSheet({
  guest,
  pending,
  onConfirm,
  onClose,
}: {
  guest: Guest | null;
  pending: boolean;
  onConfirm: (guest: Guest) => void;
  onClose: () => void;
}) {
  return (
    <BottomSheet open={guest !== null} onClose={onClose} title="Удалить гостя?">
      {guest && (
        <div className={styles.confirm}>
          <p className={styles.confirmText}>
            {guestName(guest)} больше не сможет писать на эту E-va. Вернуть доступ можно только новым invite.
          </p>
          <div className={styles.confirmActions}>
            <Button variant="destructive" loading={pending} onClick={() => onConfirm(guest)}>
              Удалить
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Отмена
            </Button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}

function guestName(guest: Guest): string {
  return guest.displayName?.trim() || "Гость без имени";
}

const issuedFormat = new Intl.DateTimeFormat("ru", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

function inviteExpiry(invite: Invite): string {
  return invite.expiresAt ? `Действует до ${formatDayMonth(new Date(invite.expiresAt))}` : "Бессрочно";
}

/** «4 доступа», как в шапке блока в макете. */
function accessLabel(count: number): string {
  return `${count} ${pluralRu(count, ["доступ", "доступа", "доступов"])}`;
}
